import { COOKIE_OPTIONS } from '@/utils/constants/cookies'

const COOKIE_ACCEPT_EVENT_STORAGE_KEY = 'emarsys_cookie_accept_event'
const cookieAcceptEventInFlight = new Map<string, Promise<boolean>>()

interface CustomSAPEventOptions {
  consentValue?: string
}

type TCustomSAPEventData = Record<string, string | undefined>

const getConsentParts = (value: string) =>
  new Set(
    value
      .split(',')
      .map((s) => s.trim().toLowerCase())
      .filter(Boolean)
  )

const hasConsent = (consentValue?: string) => {
  const gdprCookies = useCookie<string>('gdpr_settings', { ...COOKIE_OPTIONS, watch: true })
  const parts = getConsentParts(consentValue ?? gdprCookies.value ?? '')
  return parts.has('web') || parts.has('adv')
}

function whenEmarsysSdkReady<T>(run: (sdk: any) => Promise<T>): Promise<T | undefined> {
  const sdk = globalThis.window.WebEmarsysSdk
  if (!sdk) return Promise.resolve(undefined)

  return new Promise<T>((resolve, reject) => {
    sdk.push(() => {
      Promise.resolve()
        .then(() => run(globalThis.window.WebEmarsysSdk))
        .then(resolve, reject)
    })
  })
}

export function setSAPEmail(email: string) {
  if (globalThis.window === undefined) return
  if (!hasConsent() || !email) return

  globalThis.window.ScarabQueue = globalThis.window.ScarabQueue || []
  globalThis.window.ScarabQueue.push(['setEmail', email], ['go'])
}

interface ILinkedContact {
  fieldId?: number
  fieldValue?: string
}

const isKnownContact = (current?: ILinkedContact): current is Required<ILinkedContact> =>
  current?.fieldId !== undefined && Boolean(current.fieldValue)

export function syncSAPContact(pinsNumber: string): Promise<boolean> {
  if (globalThis.window === undefined) return Promise.resolve(false)
  if (!hasConsent() || !pinsNumber) return Promise.resolve(false)

  const {
    public: { sapLoginFieldID }
  } = useRuntimeConfig()
  if (!sapLoginFieldID) return Promise.resolve(false)

  const contact = { fieldId: Number(sapLoginFieldID), fieldValue: String(pinsNumber) }

  return whenEmarsysSdkReady(async (sdk) => {
    if (!(await sdk.isSubscribed())) return false

    const current = await sdk.getLoggedInContact()
    if (isKnownContact(current) && current.fieldId === contact.fieldId && current.fieldValue === contact.fieldValue) {
      return true
    }

    const linked: boolean = await sdk.login(contact)
    if (!linked) console.warn('[Emarsys] contact link rejected by SDK', contact.fieldId)

    return linked
  }).then(Boolean, (err: unknown) => {
    console.error('[Emarsys] contact link error', err)
    return false
  })
}

export function unlinkSAPContact(): Promise<boolean> {
  if (globalThis.window === undefined) return Promise.resolve(false)
  if (!hasConsent()) return Promise.resolve(false)

  return whenEmarsysSdkReady(async (sdk) => {
    if (!isKnownContact(await sdk.getLoggedInContact())) return true

    return sdk.logout()
  }).then(Boolean, (err: unknown) => {
    console.error('[Emarsys] contact unlink error', err)
    return false
  })
}

export function customSAPEvent(
  eventName: string,
  eventData: TCustomSAPEventData,
  options: CustomSAPEventOptions = {}
): Promise<boolean> {
  if (globalThis.window === undefined) return Promise.resolve(false)
  if (!eventName || !hasConsent(options.consentValue)) return Promise.resolve(false)

  return whenEmarsysSdkReady((sdk) => sdk.customEvent(eventName, eventData)).then(Boolean, (err: unknown) => {
    console.error('[Emarsys] customEvent error', err)
    return false
  })
}

export function trackCookieAcceptEvent(consentValue: string): Promise<boolean> {
  const parts = getConsentParts(consentValue)
  const web = parts.has('web')
  const adv = parts.has('adv')

  if (!web && !adv) return Promise.resolve(false)

  const eventData = { web: String(web), adv: String(adv) }
  const serializedEventData = JSON.stringify(eventData)

  try {
    if (localStorage.getItem(COOKIE_ACCEPT_EVENT_STORAGE_KEY) === serializedEventData) return Promise.resolve(false)
  } catch {
    console.warn('[Emarsys] Failed to read cookie accept event from localStorage')
  }

  const pendingEvent = cookieAcceptEventInFlight.get(serializedEventData)
  if (pendingEvent) return pendingEvent

  const event = customSAPEvent('cookie_accept', eventData, { consentValue })
    .then((success) => {
      if (success) {
        try {
          localStorage.setItem(COOKIE_ACCEPT_EVENT_STORAGE_KEY, serializedEventData)
        } catch {
          console.warn('[Emarsys] Failed to store cookie accept event in localStorage')
        }
      }
      return success
    })
    .finally(() => {
      cookieAcceptEventInFlight.delete(serializedEventData)
    })

  cookieAcceptEventInFlight.set(serializedEventData, event)

  return event
}
